import { queryOptions, skipToken } from '@tanstack/react-query';
import { QueryKey } from 'core/react-query/queryClient';
import type { MediaAsset, MediaId } from 'models/Media';
import { getMedia } from 'networking/mindfeed/services/media.service';

// Get a new URL 5 minutes before the old one expires, so it can't run out
// while a file is still downloading.
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

// Some media URLs never expire. We still ask for them again after 10 minutes.
const URL_WITHOUT_EXPIRY_REFRESH_MS = 10 * 60 * 1000;

// Remember URLs for 24 hours, even when nothing is showing them. Reusing the
// same URL lets the browser show the file from its cache.
const REMEMBER_URL_MS = 24 * 60 * 60 * 1000;

// How long until we should ask the server for a new URL.
const timeUntilRefresh = (asset?: MediaAsset): number => {
  if (!asset?.expiresAt) return URL_WITHOUT_EXPIRY_REFRESH_MS;
  const expiresIn = Date.parse(asset.expiresAt) - Date.now();
  return Math.max(0, expiresIn - REFRESH_BEFORE_EXPIRY_MS);
};

// A media file and its signed CDN URLs. Several files asked for at the same
// time are fetched in one request (see getMedia). Use with useQuery, or with
// `queryClient.fetchQuery(mediaQuery(id))` outside a component. No id:
// defined, never runs.
export const mediaQuery = (mediaId?: MediaId) =>
  queryOptions({
    queryKey: [QueryKey.MEDIA, mediaId],
    queryFn: mediaId ? () => getMedia(mediaId) : skipToken,
    staleTime: (query) => timeUntilRefresh(query.state.data),
    // Ask again shortly before the URL expires while something shows it, so
    // a link or image that has been on screen for a long time still works.
    refetchInterval: (query) => timeUntilRefresh(query.state.data) || false,
    gcTime: REMEMBER_URL_MS,
    retry: 1,
  });
